<script setup>
import { computed } from 'vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  data: {
    type: Object,
    required: true,
  },
});

const totalLeads = computed(() => {
  return (
    (props.data?.hot || 0) + (props.data?.warm || 0) + (props.data?.cold || 0)
  );
});

const hotPercentage = computed(() => {
  if (totalLeads.value === 0) return 0;
  return (((props.data?.hot || 0) / totalLeads.value) * 100).toFixed(1);
});

const warmPercentage = computed(() => {
  if (totalLeads.value === 0) return 0;
  return (((props.data?.warm || 0) / totalLeads.value) * 100).toFixed(1);
});

const coldPercentage = computed(() => {
  if (totalLeads.value === 0) return 0;
  return (((props.data?.cold || 0) / totalLeads.value) * 100).toFixed(1);
});

const averageScore = computed(() => (props.data?.average || 0).toFixed(1));
</script>

<template>
  <div class="rounded-lg border border-n-weak bg-n-solid-2 p-5">
    <!-- Header -->
    <div class="mb-4 flex items-center justify-between">
      <h3 class="text-sm font-medium text-n-slate-12">
        {{ $t('PIPELINE.ANALYTICS.LEAD_DISTRIBUTION.TITLE') }}
      </h3>
      <Icon icon="i-lucide-pie-chart" class="size-5 text-n-slate-9" />
    </div>

    <!-- Average Score -->
    <div class="mb-5 rounded-lg bg-n-alpha-2 p-4 text-center">
      <div class="text-xs text-n-slate-11">
        {{ $t('PIPELINE.ANALYTICS.LEAD_DISTRIBUTION.AVERAGE_SCORE') }}
      </div>
      <div class="text-3xl font-semibold text-n-slate-12">
        {{ averageScore
        }}<span class="text-lg text-n-slate-9">{{
          $t('PIPELINE.ANALYTICS.LEAD_DISTRIBUTION.OUT_OF_100')
        }}</span>
      </div>
    </div>

    <!-- Distribution Bars -->
    <div class="space-y-3">
      <!-- Hot Leads -->
      <div class="rounded-lg border border-n-ruby-6 bg-n-ruby-3 p-3">
        <div class="mb-2 flex items-center justify-between">
          <div class="flex items-center gap-2">
            <div class="h-2.5 w-2.5 rounded-full bg-n-ruby-9" />
            <span class="text-xs font-medium text-n-ruby-11">
              {{ $t('PIPELINE.ANALYTICS.LEAD_DISTRIBUTION.HOT_LEADS') }}
            </span>
          </div>
          <span class="text-base font-semibold text-n-ruby-11">
            {{ data?.hot || 0 }}
          </span>
        </div>
        <div class="h-1.5 overflow-hidden rounded-full bg-n-ruby-6">
          <div
            class="h-full bg-n-ruby-9 transition-all duration-500"
            :style="{ width: `${hotPercentage}%` }"
          />
        </div>
        <div class="mt-1 text-right text-[10px] text-n-ruby-11">
          {{
            $t('PIPELINE.ANALYTICS.LEAD_DISTRIBUTION.HOT_RANGE', {
              percentage: hotPercentage,
            })
          }}
        </div>
      </div>

      <!-- Warm Leads -->
      <div class="rounded-lg border border-n-amber-6 bg-n-amber-3 p-3">
        <div class="mb-2 flex items-center justify-between">
          <div class="flex items-center gap-2">
            <div class="h-2.5 w-2.5 rounded-full bg-n-amber-9" />
            <span class="text-xs font-medium text-n-amber-11">
              {{ $t('PIPELINE.ANALYTICS.LEAD_DISTRIBUTION.WARM_LEADS') }}
            </span>
          </div>
          <span class="text-base font-semibold text-n-amber-11">
            {{ data?.warm || 0 }}
          </span>
        </div>
        <div class="h-1.5 overflow-hidden rounded-full bg-n-amber-6">
          <div
            class="h-full bg-n-amber-9 transition-all duration-500"
            :style="{ width: `${warmPercentage}%` }"
          />
        </div>
        <div class="mt-1 text-right text-[10px] text-n-amber-11">
          {{
            $t('PIPELINE.ANALYTICS.LEAD_DISTRIBUTION.WARM_RANGE', {
              percentage: warmPercentage,
            })
          }}
        </div>
      </div>

      <!-- Cold Leads -->
      <div class="rounded-lg border border-n-blue-6 bg-n-blue-3 p-3">
        <div class="mb-2 flex items-center justify-between">
          <div class="flex items-center gap-2">
            <div class="h-2.5 w-2.5 rounded-full bg-n-blue-9" />
            <span class="text-xs font-medium text-n-blue-11">
              {{ $t('PIPELINE.ANALYTICS.LEAD_DISTRIBUTION.COLD_LEADS') }}
            </span>
          </div>
          <span class="text-base font-semibold text-n-blue-11">
            {{ data?.cold || 0 }}
          </span>
        </div>
        <div class="h-1.5 overflow-hidden rounded-full bg-n-blue-6">
          <div
            class="h-full bg-n-blue-9 transition-all duration-500"
            :style="{ width: `${coldPercentage}%` }"
          />
        </div>
        <div class="mt-1 text-right text-[10px] text-n-blue-11">
          {{
            $t('PIPELINE.ANALYTICS.LEAD_DISTRIBUTION.COLD_RANGE', {
              percentage: coldPercentage,
            })
          }}
        </div>
      </div>
    </div>
  </div>
</template>
